import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { TrendUp, ArrowLeft, CheckCircle, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

function fmtUptime(sec) {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function Dot({ ok }) {
  return <span className={`h-2 w-2 rounded-full ${ok ? "bg-signal-green animate-pulse" : "bg-signal-red"}`} />;
}

export default function Status() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/status");
      setData(r.data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
      setCheckedAt(new Date());
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const apiOk = !error && !!data;
  const dbOk = apiOk && data?.database === "connected";
  const allOk = apiOk && dbOk;

  const checks = [
    { name: "API service", ok: apiOk, detail: apiOk ? "Responding" : "Unreachable" },
    { name: "Database", ok: dbOk, detail: dbOk ? "Connected" : "Unavailable" },
    { name: "Web app", ok: true, detail: "Serving" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="status-page">
      <header className="border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center bg-volt rounded-sm"><TrendUp size={18} weight="bold" className="text-white" /></div>
            <span className="font-heading font-bold">MarketPilot<span className="text-volt"> AI</span></span>
          </Link>
          <Link to="/" data-testid="status-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors duration-200"><ArrowLeft size={15} /> Back to app</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <p className="mono text-[11px] uppercase tracking-widest text-muted-foreground">System Status</p>

        {/* Overall banner */}
        <div
          data-testid="status-banner"
          className={`mt-4 flex items-center gap-4 border p-5 rounded-sm ${
            loading ? "border-border bg-card" : allOk
              ? "border-signal-green/40 bg-signal-green/10"
              : "border-signal-yellow/40 bg-signal-yellow/10"
          }`}
        >
          {loading ? (
            <ArrowClockwise size={26} className="text-muted-foreground animate-spin" />
          ) : allOk ? (
            <CheckCircle size={30} weight="fill" className="text-signal-green" />
          ) : (
            <WarningCircle size={30} weight="fill" className="text-signal-yellow" />
          )}
          <div>
            <div className="font-heading text-lg font-semibold" data-testid="status-headline">
              {loading ? "Checking systems…" : allOk ? "All systems operational" : "Partial service disruption"}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">
              {loading ? "Contacting the platform" : allOk
                ? "Everything is running normally."
                : "Some components are degraded. We're on it."}
            </div>
          </div>
        </div>

        {/* Service checks */}
        <div className="mt-8">
          <h2 className="label-xs mb-3">Components</h2>
          <div className="border border-border rounded-sm divide-y divide-border" data-testid="status-components">
            {checks.map((c) => (
              <div key={c.name} className="flex items-center justify-between px-4 py-3.5" data-testid={`status-check-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="text-sm">{c.name}</span>
                <span className="inline-flex items-center gap-2 mono text-xs">
                  <span className={c.ok ? "text-signal-green" : "text-signal-red"}>{c.detail}</span>
                  <Dot ok={c.ok} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Deploy / runtime info */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          <div className="border border-border bg-card p-4">
            <div className="label-xs">Build version</div>
            <div className="mt-1.5 mono text-sm font-semibold text-volt truncate" data-testid="status-version" title={data?.version || ""}>
              {data?.version ? data.version.slice(0, 12) : "—"}
            </div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="label-xs">Uptime</div>
            <div className="mt-1.5 mono text-sm font-semibold" data-testid="status-uptime">
              {apiOk ? fmtUptime(data?.uptime_seconds) : "—"}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between mono text-xs text-muted-foreground">
          <span data-testid="status-checked-at">
            Last checked {checkedAt ? checkedAt.toLocaleTimeString() : "—"}
          </span>
          <button
            onClick={load}
            data-testid="status-refresh"
            className="inline-flex items-center gap-1.5 hover:text-white transition-colors duration-200"
          >
            <ArrowClockwise size={13} /> Refresh
          </button>
        </div>
      </main>
    </div>
  );
}
